import { useMemo } from "react";

import Box from "@/components/Box";
import { ExternalTextLink, InternalTextLink } from "@/components/Links";
import { SpanText } from "@/components/Text";
import { CONSULT_NEEDED, RESULTS_AVAILABLE } from "@/core/utils";
import Group from "@/tpo/Group";
import Stack from "@/tpo/Stack";
import { useTestStatuses } from "@/tpo/TestStatuses";

function RelatedTestRow({ row, getOrderUrl, getTestResultUrl }) {
  const resultsAvailable = row.status === RESULTS_AVAILABLE || row.status === CONSULT_NEEDED;
  const statusIsLink = row.isUserTest && row.isLink;

  return (
    <Group
      justifyContent="space-between"
      alignItems="flex-start"
      gap={16}
      py={20}
      borderBottom="1px solid #F2F2F2"
    >
      <Stack flex={1} minWidth={0} fontFamily="gilroyMedium" gap={4}>
        <Box fontFamily="inherit" lineHeight="24px" fontSize={14}>
          {row.name}
        </Box>
        {(!!row.orderId || row.userTest?.externalSourceTest || row.created) && (
          <Group fontFamily="inherit" flexWrap="wrap" alignItems="center">
            {row.userTest?.externalSourceTest ? (
              <SpanText fontFamily="inherit" lineHeight={1} color="#818181" fontSize={14}>
                External source
              </SpanText>
            ) : (
              !!row.orderId && (
                <ExternalTextLink
                  fontFamily="inherit"
                  lineHeight={1}
                  color="#818181"
                  fontSize={14}
                  newTab
                  href={getOrderUrl(row.orderId)}
                >
                  {row.orderId}
                </ExternalTextLink>
              )
            )}
            {(!!row.orderId || row.userTest?.externalSourceTest) && row.created && (
              <SpanText fontFamily="inherit" lineHeight={1} color="#818181" mx={1} fontSize={14}>
                |
              </SpanText>
            )}
            {row.created && (
              <SpanText fontFamily="inherit" lineHeight={1} color="#818181" fontSize={14}>
                {row.created}
              </SpanText>
            )}
          </Group>
        )}
      </Stack>
      <Box textAlign="right" flexShrink={0}>
        {statusIsLink ? (
          <InternalTextLink
            href={getTestResultUrl(row.userTest.id)}
            fontFamily="gilroyMedium"
            fontSize={14}
            lineHeight="24px"
            color={resultsAvailable ? "green" : "dark"}
            underline
          >
            {row.status}
          </InternalTextLink>
        ) : (
          <SpanText fontFamily="gilroyMedium" fontSize={14} lineHeight="24px" color="dark">
            {row.status}
          </SpanText>
        )}
      </Box>
    </Group>
  );
}

export default function RelatedTestsList({
  userTests,
  ordersInProgress,
  getOrderUrl,
  getTestResultUrl
}) {
  const rows = useTestStatuses({
    testResults: userTests,
    ordersInProgress
  });

  const rowKeys = useMemo(
    () =>
      rows.map((row, index) =>
        row.isUserTest ? `user-test-${row.id}` : `order-test-item-${row.id}-${index}`
      ),
    [rows]
  );

  return (
    <Stack gap={0}>
      {rows.map((row, index) => (
        <RelatedTestRow
          key={rowKeys[index]}
          row={row}
          getOrderUrl={getOrderUrl}
          getTestResultUrl={getTestResultUrl}
        />
      ))}
    </Stack>
  );
}
